
import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  Chip,
  Button,
} from "@mui/material";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";

const PAGE_SIZE = 5;
const TABLE_MAX_HEIGHT = 400;

export default function MostOccupiedWorkstations({ workspaces = [] }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const tableContainerRef = useRef(null);

  // Calculate occupancy and rank workspaces.
  const topWorkspaces = useMemo(() => {
    return workspaces
      .map((workspace) => {
        const seats = Array.isArray(workspace.workstations)
          ? workspace.workstations
          : [];

        const totalSeats = seats.length;

        const occupiedSeats = seats.filter(
          (seat) =>
            String(seat.status ?? "").toLowerCase() === "occupied"
        ).length;

        const occupancy =
          totalSeats > 0
            ? (occupiedSeats / totalSeats) * 100
            : 0;

        return {
          id: workspace.id,
          name: workspace.name || "Unnamed Workspace",
          totalSeats,
          occupiedSeats,
          occupancy,
        };
      })
      .filter((workspace) => workspace.totalSeats > 0)
      .sort(
        (a, b) =>
          b.occupancy - a.occupancy ||
          b.occupiedSeats - a.occupiedSeats
      );
  }, [workspaces]);

  // Show only the requested number of rows.
  const visibleWorkspaces = useMemo(
    () => topWorkspaces.slice(0, visibleCount),
    [topWorkspaces, visibleCount]
  );

  const handleLoadMore = () => {
  setVisibleCount((count) =>
    Math.min(count + PAGE_SIZE, topWorkspaces.length)
  );
};

  const handleLoadLess = () => {
    setVisibleCount(PAGE_SIZE);

    // Reset the table's internal scroll position.
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const getStatus = (occupancy) => {
    if (occupancy >= 80) {
      return {
        label: "High",
        background: "#D1FAE5",
        textColor: "#059669",
      };
    }

    if (occupancy >= 60) {
      return {
        label: "Medium",
        background: "#FEF3C7",
        textColor: "#D97706",
      };
    }

    return {
      label: "Low",
      background: "#DBEAFE",
      textColor: "#2563EB",
    };
  };

  return (
    <Card
      elevation={0}
      
sx={{
  width: "100%",
  height: 560,
  minHeight: 560,
  maxHeight: 560,
  minWidth: 0,
  boxSizing: "border-box",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #E2E8F0",
  borderRadius: 3,
  background:
    "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
}}

    >
      <CardContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          "&:last-child": { pb: { xs: 1.5, sm: 2.5 } },
          width: "100%",
          boxSizing: "border-box",
          flex: 1,
  minHeight: 0,
  overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              backgroundColor: "#EAF2FF",
              color: "#2580F7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <EmojiEventsRoundedIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              Most Occupied Workspaces
            </Typography>

            <Typography
              sx={{ fontSize: 12, color: "#64748B", mt: 0.3 }}
            >
              Top workspaces ranked by occupancy
            </Typography>
          </Box>
        </Box>

        {/* Scrollable table */}
        <Box
          ref={tableContainerRef}
          sx={{
    width: "100%",
    height: 300,
    minHeight: 300,
    maxHeight: 400,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #DCE5F1",
    borderRadius: 2,
    boxSizing: "border-box",
  }}
>
          <Table
            size="medium"
            stickyHeader
            sx={{
              width: "100%",
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                borderBottom: "1px solid #E8EEF5",
                py: 1.6,
                px: { xs: 1, sm: 2 },
                color: "#1E293B",
                fontSize: { xs: 12, sm: 13 },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: "8%",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  #
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: "32%",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Workspace
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: "42%",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Occupancy
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: "18%",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleWorkspaces.length > 0 ? (
                visibleWorkspaces.map((workspace, index) => {
                  const status = getStatus(workspace.occupancy);

                  return (
                    <TableRow
                      key={workspace.id || workspace.name}
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        "&:hover": {
                          backgroundColor: "#F8FAFC",
                        },
                      }}
                    >
                      <TableCell sx={{ color: "#64748B" }}>
                        {index + 1}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 600,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {workspace.name}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: {
                              xs: "column",
                              sm: "row",
                            },
                            alignItems: {
                              xs: "stretch",
                              sm: "center",
                            },
                            gap: 1.2,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              minWidth: { sm: 100 },
                            }}
                          >
                            {Math.round(workspace.occupancy)}% (
                            {workspace.occupiedSeats}/
                            {workspace.totalSeats})
                          </Typography>

                          <LinearProgress
                            variant="determinate"
                            value={workspace.occupancy}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              height: 9,
                              borderRadius: 5,
                              backgroundColor: "#E2E8F0",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 5,
                                background:
                                  "linear-gradient(90deg, #10B981, #059669)",
                              },
                            }}
                          />
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: 11,
                            fontWeight: 700,
                            borderRadius: 1.5,
                            backgroundColor: status.background,
                            color: status.textColor,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#94A3B8" }}
                    >
                      No workspace occupancy data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Load More / Load Less controls */}
        {topWorkspaces.length > PAGE_SIZE && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mt: 2.5,
            }}
          >
            {visibleCount < topWorkspaces.length && (
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  borderRadius: 2,
                  borderColor: "#CBD5E1",
                  color: "#40518A",
                  "&:hover": {
                    borderColor: "#40518A",
                    backgroundColor: "#F1F5F9",
                  },
                }}
              >
                Load More (
                {topWorkspaces.length - visibleCount} remaining)
              </Button>
            )}

            {visibleCount > PAGE_SIZE && (
              <Button
                variant="outlined"
                onClick={handleLoadLess}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  borderRadius: 2,
                  borderColor: "#CBD5E1",
                  color: "#40518A",
                  "&:hover": {
                    borderColor: "#40518A",
                    backgroundColor: "#F1F5F9",
                  },
                }}
              >
                Load Less
              </Button>
            )}
          </Box>
        )}

        {topWorkspaces.length > PAGE_SIZE &&
          visibleCount >= topWorkspaces.length && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 2,
                color: "#94A3B8",
              }}
            >
              All {topWorkspaces.length} workspaces displayed
            </Typography>
          )}
      </CardContent>
    </Card>
  );
}
